import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { getCookiebox } from "../../../state/reducers/cookiebox";

// Détecte la présence d'au moins une balise <script> (ouvrante)
const HAS_SCRIPT_REGEX = /<script\b/i;
// Détecte la présence d'au moins une balise <iframe> (ouvrante)
const HAS_IFRAME_REGEX = /<iframe\b/i;
// Capture les balises <script>…</script> (avec ou sans contenu) pour les retirer
const SCRIPT_TAG_REGEX = /<script\b[^>]*>[\s\S]*?<\/script>/gi;

// Message identique à celui utilisé pour les iframes (utils/functions.js → iframeParse)
const CONSENT_MESSAGE =
  'Cet élément a besoin des cookies pour fonctionner, vous devez accepter notre utilisation des cookies pour l\'afficher en cliquant sur le bouton "Accepter" en bas à droite de votre écran.';

// Recrée un <script> exécutable à partir d'un nœud inerte (issu d'un parsing innerHTML).
// Un <script> inséré via innerHTML n'est jamais exécuté par le navigateur : il faut
// le reconstruire via document.createElement pour déclencher son exécution.
const buildExecutableScript = sourceScript => {
  const script = document.createElement("script");
  for (let i = 0; i < sourceScript.attributes.length; i += 1) {
    const { name, value } = sourceScript.attributes[i];
    script.setAttribute(name, value);
  }
  if (sourceScript.textContent) {
    script.textContent = sourceScript.textContent;
  }
  return script;
};

/**
 * Rend du HTML brut (bloc core/html) dont les <script> doivent réellement
 * s'exécuter — ce que React / dangerouslySetInnerHTML ne font jamais.
 *
 * - Les <script> sont retirés du markup rendu (donc absents du SSR) puis
 *   réinjectés côté client via useEffect, garantissant une exécution unique
 *   au chargement direct comme en navigation SPA.
 * - L'exécution est conditionnée au consentement cookies (cookiebox === false),
 *   de façon cohérente avec le traitement des iframes.
 *
 * Hydratation : le consentement dépend du localStorage, inconnu au build SSR
 * (cookiebox vaut alors `true` par défaut → le markup statique contient le
 * message « consentement requis »). Or React 16 ne réconcilie jamais un
 * dangerouslySetInnerHTML divergent pendant l'hydratation en production : le
 * message resterait donc figé même après acceptation. On force le remontage
 * client (via `key`) des contenus dynamiques (script/iframe) une fois hydraté,
 * pour que React applique l'état de consentement réel plutôt que le rendu de
 * build.
 */
export const RawHtml = ({ html = "", blockName = "", cookiebox }) => {
  const containerRef = useRef(null);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const consentGiven = cookiebox === false;
  const hasScript = HAS_SCRIPT_REGEX.test(html);
  const htmlWithoutScripts = html.replace(SCRIPT_TAG_REGEX, "");
  const label = `Contenu dynamique du bloc ${blockName}`;
  // Contenu dont le rendu dépend du consentement (donc divergent SSR/client) :
  // on le remonte côté client. Le HTML statique (paragraphes, titres…) n'est pas
  // concerné et n'est pas remonté inutilement.
  const isDynamic = hasScript || HAS_IFRAME_REGEX.test(html);
  const renderKey = hydrated && isDynamic ? "client" : "ssr";

  useEffect(() => {
    const container = containerRef.current;
    if (!hydrated || !container || !consentGiven || !hasScript) {
      return undefined;
    }

    const parser = document.createElement("div");
    parser.innerHTML = html; // scripts inertes, simplement parsés
    const appended = [];
    parser.querySelectorAll("script").forEach(sourceScript => {
      const script = buildExecutableScript(sourceScript);
      container.appendChild(script);
      appended.push(script);
    });

    return () => {
      appended.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [hydrated, html, consentGiven, hasScript]);

  if (hasScript && !consentGiven) {
    return (
      <div key={renderKey} role="region" aria-label={label}>
        <div className="border border-primary bg-light p-5 mb-5">
          <span className="h5">{CONSENT_MESSAGE}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      key={renderKey}
      ref={containerRef}
      role="region"
      aria-label={label}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: htmlWithoutScripts }}
    />
  );
};

const mapStateToProps = state => ({
  cookiebox: getCookiebox(state)
});

export default connect(mapStateToProps)(RawHtml);
